import React, { useEffect, useState } from 'react';
import { Truck, IndianRupee, Users, AlertCircle, TrendingUp, Sparkles } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, hint, accent = 'blue' }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
    <div className={`w-10 h-10 rounded-xl grid place-items-center mb-3 ${accent === 'green' ? 'bg-rozops-green-light text-rozops-green' : accent === 'red' ? 'bg-red-50 text-red-500' : accent === 'purple' ? 'bg-rozops-accent-light text-rozops-accent' : 'bg-rozops-blue-light text-rozops-blue'}`}>
      <Icon className="w-5 h-5" strokeWidth={2.2} />
    </div>
    <div className="text-xs uppercase tracking-widest text-rozops-text-muted font-bold">{label}</div>
    <div className="font-heading font-extrabold text-2xl text-rozops-navy mt-1">{value}</div>
    {hint && <div className="text-xs text-rozops-text-muted mt-1">{hint}</div>}
  </div>
);

export default function MerchantDashboard() {
  const [stats, setStats] = useState(null);
  const [ai, setAi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(true);
  useEffect(() => {
    apiGet('/merchant/dashboard')
      .then(r => setStats(r.data))
      .catch(e => toast.error(e.message))
      .finally(() => setLoading(false));

    apiGet('/ai/dashboard')
      .then(r => setAi(r.data))
      .catch(() => setAi(null))
      .finally(() => setAiLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="merchant-dashboard">
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Today's overview</h1>
        <p className="text-sm text-rozops-text-muted mt-1">A quick snapshot of your daily operations.</p>
      </div>
      {loading ? (
        <div className="text-rozops-text-muted">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Truck} label="Deliveries today" value={`${stats?.completed_today || 0} / ${stats?.deliveries_today || 0}`} hint="Completed / Planned" accent="purple" />
            <StatCard icon={Users} label="Active customers" value={stats?.active_customers || 0} accent="green" />
            <StatCard icon={IndianRupee} label="Revenue (this month)" value={`₹${(stats?.revenue_month || 0).toLocaleString('en-IN')}`} accent="green" />
            <StatCard icon={AlertCircle} label="Outstanding" value={`₹${(stats?.outstanding || 0).toLocaleString('en-IN')}`} accent="red" />
          </div>
          {(aiLoading || ai?.cards?.length > 0) && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
              <div className="flex items-center gap-2 text-rozops-accent text-sm font-semibold">
                <Sparkles className="w-4 h-4" /> AI business summary
              </div>
              {aiLoading ? (
                <div className="mt-4 text-sm text-rozops-text-muted">Loading AI summary…</div>
              ) : (
                <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
                  {ai.cards.map(card => (
                    <div key={card.type} className="rounded-xl border border-gray-100 bg-rozops-bg p-4">
                      <div className="text-xs uppercase tracking-wider text-rozops-text-muted font-bold">{card.title}</div>
                      <div className="mt-2 font-heading font-extrabold text-xl text-rozops-navy">
                        {typeof card.value === 'number' && card.type.includes('revenue') ? `₹${card.value.toLocaleString('en-IN')}` : card.value}
                      </div>
                      <div className="mt-1 text-xs text-rozops-text-muted">{card.detail}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
            <div className="flex items-center gap-2 text-rozops-accent text-sm font-semibold"><TrendingUp className="w-4 h-4" /> Get started</div>
            <div className="mt-2 font-heading font-bold text-xl text-rozops-navy">Add customers, products, and subscriptions to unlock deliveries and billing.</div>
            <div className="mt-4 text-sm text-rozops-text-muted">Use the menu on the left to navigate.</div>
          </div>
        </>
      )}
    </div>
  );
}
